import React, { useState } from 'react';
import { Device, DeviceStatus, DeviceType } from '../../types/device';
import { Server, Clock, Activity, Search, Settings, Link2, Box, Check, Filter, X } from 'lucide-react';
import DeviceManagementModal from './DeviceManagementModal';
import { mockDevices } from '../../data/mockDevices';
import { Checkbox } from '../../../../components/ui/checkbox';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../../../components/ui/select';
import { Input } from '../../../../components/ui/input';
import { Button } from '../../../../components/ui/button';
const statusColors: Record<DeviceStatus, string> = {
  connected: 'bg-green-500',
  disconnected: 'bg-red-500',
  communicating: 'bg-blue-500',
  commissioned: 'bg-purple-500',
  decommissioned: 'bg-gray-500'
};

const DeviceManagement: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<DeviceStatus | 'all'>('all');
  const [typeFilter, setTypeFilter] = useState<DeviceType | 'all'>('all');
  const [selectedDevices, setSelectedDevices] = useState<string[]>([]);
  const [bulkStatus, setBulkStatus] = useState<DeviceStatus | ''>('');
  const [bulkProject, setBulkProject] = useState('');
  
  // Extract unique projects from mock data
  const availableProjects = Array.from(
    new Map(
      mockDevices
        .filter(d => d.project)
        .map(d => d.project!)
        .map(project => [project.id, { id: project.id, name: project.name }])
    ).values()
  );

  const filteredDevices = mockDevices.filter(device => {
    const matchesSearch = device.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         device.id.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || device.status === statusFilter;
    const matchesType = typeFilter === 'all' || device.type === typeFilter;
    return matchesSearch && matchesStatus && matchesType;
  });

  // Check if there are active filters
  const hasActiveFilters = searchTerm !== '' || statusFilter !== 'all' || typeFilter !== 'all';

  // Handle clearing all filters
  const handleClearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setTypeFilter('all');
  };

  const handleDeviceSelect = (deviceId: string) => {
    setSelectedDevices(prev => 
      prev.includes(deviceId) 
        ? prev.filter(id => id !== deviceId)
        : [...prev, deviceId]
    );
  };

  const handleSelectAll = () => {
    if (selectedDevices.length === filteredDevices.length) {
      setSelectedDevices([]);
    } else {
      setSelectedDevices(filteredDevices.map(d => d.id));
    }
  };

  const handleBulkStatusUpdate = () => {
    if (!bulkStatus || selectedDevices.length === 0) return;
    mockDevices.forEach(device => {
      if (selectedDevices.includes(device.id)) {
        device.status = bulkStatus;
      }
    });
    setSelectedDevices([]);
    setBulkStatus('');
  };

  const handleBulkProjectAssign = () => {
    if (!bulkProject || selectedDevices.length === 0) return;
    const targetProject = availableProjects.find(p => p.id === bulkProject);
    if (!targetProject) return;
    
    mockDevices.forEach(device => {
      if (selectedDevices.includes(device.id)) {
        device.project = {
          id: targetProject.id,
          name: targetProject.name,
          status: 'active',
          startDate: new Date().toISOString().split('T')[0]
        };
      }
    });
    setSelectedDevices([]);
    setBulkProject('');
  };

  const getStatusBadge = (status: DeviceStatus) => (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusColors[status]} text-white`}>
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );

  return (
    <div className="p-6 w-full">
      {/* Header with stats */}
      <div className="grid grid-cols-5 gap-4 mb-6">
        {Object.entries(statusColors).map(([status, color]) => (
          <div key={status} className="bg-white rounded-lg shadow p-4 transition-transform hover:scale-105">
            <div className="flex items-center justify-between">
              <span className="text-gray-500 capitalize">{status}</span>
              <span className={`w-3 h-3 rounded-full ${color}`}></span>
            </div>
            <div className="mt-2 text-2xl font-bold">
              {mockDevices.filter(d => d.status === status).length}
            </div>
          </div>
        ))}
      </div>

      {/* Filters and Bulk Actions */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        {/* Filters Header with consistent height */}
        <div className="flex items-center justify-between mb-4 h-8">
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-gray-500" />
            <span className="font-medium">Filters</span>
          </div>
          <div className="h-8 flex items-center">
            {hasActiveFilters && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClearFilters}
                className="text-gray-500 hover:text-gray-700 transition-opacity duration-200"
              >
                <X className="h-4 w-4 mr-1" />
                Clear All
              </Button>
            )}
          </div>
        </div>

        <div className="flex flex-col md:flex-row gap-4">
        <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
              <Input
                type="text"
                placeholder="Search devices..."
                className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#e04530]"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          
          <Select value={statusFilter} onValueChange={(value) => setStatusFilter(value as DeviceStatus | 'all')}>
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="All Statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="connected">Connected</SelectItem>
              <SelectItem value="disconnected">Disconnected</SelectItem>
              <SelectItem value="communicating">Communicating</SelectItem>
              <SelectItem value="commissioned">Commissioned</SelectItem>
              <SelectItem value="decommissioned">Decommissioned</SelectItem>
            </SelectContent>
          </Select>

          <Select value={typeFilter} onValueChange={(value) => setTypeFilter(value as DeviceType | 'all')}>
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="All Types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="welding_device">Welding Device</SelectItem>
              <SelectItem value="coating_device">Coating Device</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Bulk Actions with smooth transition */}
        <div className={`mt-4 transition-all duration-200 ease-in-out ${selectedDevices.length > 0 ? 'opacity-100 max-h-96' : 'opacity-0 max-h-0 overflow-hidden'}`}>
          <div className="flex flex-col gap-4 p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">{selectedDevices.length} devices selected</span>
            </div>
            
            <div className="flex flex-wrap gap-4">
              {/* Status Update */}
              <div className="flex items-center gap-2">
                <Select 
                  value={bulkStatus || 'status_placeholder'}
                  onValueChange={(value) => {
                    if (value !== 'status_placeholder') {
                      setBulkStatus(value as DeviceStatus);
                    }
                  }}
                >
                  <SelectTrigger className="w-[200px]">
                    <SelectValue placeholder="Change Status To..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="status_placeholder">Change Status To...</SelectItem>
                    <SelectItem value="connected">Connected</SelectItem>
                    <SelectItem value="disconnected">Disconnected</SelectItem>
                    <SelectItem value="communicating">Communicating</SelectItem>
                    <SelectItem value="commissioned">Commissioned</SelectItem>
                    <SelectItem value="decommissioned">Decommissioned</SelectItem>
                  </SelectContent>
                </Select>
                <Button
                  onClick={handleBulkStatusUpdate}
                  disabled={!bulkStatus}
                  variant="default"
                >
                  Update Status
                </Button>
              </div>

              {/* Project Assignment */}
              <div className="flex items-center gap-2">
                <Select 
                  value={bulkProject || 'project_placeholder'}
                  onValueChange={(value) => {
                    if (value !== 'project_placeholder') {
                      setBulkProject(value);
                    }
                  }}
                >
                  <SelectTrigger className="w-[200px]">
                    <SelectValue placeholder="Assign to Project..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="project_placeholder">Assign to Project...</SelectItem>
                    {availableProjects.map(project => (
                      <SelectItem key={project.id} value={project.id}>
                        {project.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  onClick={handleBulkProjectAssign}
                  disabled={!bulkProject}
                  variant="default"
                >
                  Assign Project
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Select All */}
      <div className="flex items-center gap-2 mb-4">
        <div className="flex items-center space-x-2">
          <Checkbox
            id="select-all-devices"
            checked={selectedDevices.length === filteredDevices.length && filteredDevices.length > 0}
            onCheckedChange={handleSelectAll}
          />
          <label
            htmlFor="select-all-devices"
            className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
          >
            Select All
          </label>
        </div>
      </div>

      {/* Device List */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {filteredDevices.map(device => (
          <div key={device.id} className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow flex flex-col h-full relative">
            {/* Checkbox for selection */}
            <div className="absolute top-4 left-4">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id={`select-device-${device.id}`}
                  checked={selectedDevices.includes(device.id)}
                  onCheckedChange={() => handleDeviceSelect(device.id)}
                />
              </div>
            </div>
            
            <div className="p-4 flex-grow">
              <div className="flex items-center justify-between mb-4 pl-8">
                <h3 className="text-xs font-mono font-bold text-gray-600 break-all max-w-[70%]">{device.id}</h3>
                {getStatusBadge(device.status)}
              </div>
              
              <div className="space-y-2 text-sm text-gray-600">
                <div className="flex items-center gap-2">
                  <Server className="h-4 w-4" />
                  <span>{device.name}</span>
                </div>
                {device.connectedEquipment && (
                  <div className="flex items-center gap-2">
                    <Link2 className="h-4 w-4" />
                    <span>{device.connectedEquipment.serialNumber} | Firmware: v1.0.0</span>
                  </div>
                )}
                {device.project && (
                  <div className="flex items-center gap-2">
                    <Box className="h-4 w-4" />
                    <span>Project: {device.project.name}</span>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  <span>Last seen: {new Date(device.lastSeen).toLocaleString()}</span>
                </div>
              </div>
            </div>
            
            <div className="bg-gray-50 px-4 py-3 rounded-b-lg mt-auto">
              <div className="flex items-center justify-between text-sm">
                <div className="text-gray-500">
                  <div>Firmware: {device.metadata.firmware}</div>
                </div>
                <DeviceManagementModal
                  device={device}
                  onStatusChange={(status) => {
                    // Here you would typically make an API call
                    device.status = status;
                    // Force a re-render
                    setSelectedDevices([...selectedDevices]);
                  }}
                  onReboot={() => {
                    // Here you would typically make an API call
                    console.log('Rebooting device:', device.id);
                  }}
                  onFirmwareUpdate={() => {
                    // Here you would typically make an API call
                    console.log('Checking for firmware updates:', device.id);
                  }}
                />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default DeviceManagement; 